/**
 * WebPlacementDialog Component
 *
 * Dialog for creating or editing a web placement.
 * Web placements generate shareable URLs for onboarding funnels.
 *
 * Architecture: "DEFINE → USE" Pattern
 * 1. DEFINE parameters in source sections (URL, Server, V2 Variables, Custom)
 * 2. USE/MAP parameters in destination sections (Redirect, Stripe, Webhooks)
 *
 * Layout:
 * - Basic: Name, alias
 * - Redirect URL: Fallback URL + conditions (expanded by default)
 * - Onboarding Selection
 * - Parameter Definitions: source type accordions
 * - Destinations: Redirect params, Stripe metadata, Webhooks
 * - Advanced: SEO
 *
 * @module webplacements/components
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import HomeIcon from '@mui/icons-material/Home';
import CloudIcon from '@mui/icons-material/Cloud';
import DownloadIcon from '@mui/icons-material/Download';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import CodeIcon from '@mui/icons-material/Code';
import PaymentIcon from '@mui/icons-material/Payment';
import EmailIcon from '@mui/icons-material/Email';
import PolicyIcon from '@mui/icons-material/Policy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CasinoIcon from '@mui/icons-material/Casino';
import { useTheme } from '@mui/material/styles';
import { SDialog, WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { useCustomDomain } from '@modules/custom-domains';
import { OnboardingPickerButton, type OnboardingSelection } from '@modules/placements/components/OnboardingPicker';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { FlowVariablesService } from '@modules/firebase-core/services/FlowVariablesService';
import { StepNodeScreenService } from '@modules/firebase-core/services/StepNodeScreenService';
import { detectSchemaVersion } from '@modules/webplacements/services/schemaVersionDetector';
import type { FlowDocument, FunnelVariable } from '@segmently/schema-models/v2';
import type {
  WebPlacement,
  WebPlacementConfig,
  CreateWebPlacementInput,
  UpdateWebPlacementInput,
  WebDataCollectionConfig,
  WebCustomAnalyticsConfig,
  WebSEOConfig,
  WebConditionalRedirectConfig,
  ParameterDefinition,
  WebStripeMetadataConfig,
  WebhookConfig,
  WebStripeConfig,
  VariableBinding,
  EntryScreenRule,
} from '@modules/webplacements/types/webplacements.types';
import type { ConsentManagementConfig } from '@modules/analytics-settings/types/analytics.types';
// Source section editors (DEFINE parameters)
import {
  UrlParametersSection,
  ServerDataSection,
  VariableInputsSection,
  CustomVariablesSection,
  AttributionTogglesSection,
} from './editors/sources';
import {
  applyToggles,
  collectProtectedKeys,
  type AttributionConfigSlice,
  type AttributionPresetKey,
  type AttributionToggleState,
} from '@modules/webplacements/config/attributionPresets';
import { useAttributionSeed } from '@modules/webplacements/hooks/useAttributionSeed';
// Destination section editors (USE parameters)
import {
  RedirectParametersSection,
  StripeMetadataSection,
  WebhooksSection,
  VariableBindingsSection,
  type DestinationParameterMapping,
} from './editors/destinations';
import { EntryScreenRulesSection } from './editors/destinations/EntryScreenRulesSection';
// Core editors
import { RedirectUrlSection } from './editors/RedirectUrlSection';
import { SEOEditor } from './editors/SEOEditor';
import { ConsentManagementEditor } from './editors/ConsentManagementEditor';
import {
  WEB_PLACEMENT_VALIDATION,
  isValidWebAlias,
  getFunnelUrl,
  getLocalDevFunnelUrl,
  getWebPlacementSdkConfigUrl,
} from '@modules/webplacements/types/webplacements.types';
import { VariableEditorProvider, useVariableEditor } from '@modules/reactFlow/contexts/VariableEditorContext';
import { V2VariableCrudPanel } from '@modules/v2-variable-utils';
import {
  formatWebPlacementReferenceIssues,
  validateWebPlacementReferences,
} from '@modules/webplacements/services/webPlacementReferenceValidation';

/**
 * Props for WebPlacementDialog
 */
export interface WebPlacementDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** Called when web placement is created (create mode) */
  onCreate?: (data: Omit<CreateWebPlacementInput, 'createdBy'>) => Promise<WebPlacement>;
  /** Called when web placement is updated (edit mode) */
  onUpdate?: (id: string, data: UpdateWebPlacementInput) => Promise<WebPlacement>;
  /** Called after successful save to trigger deployment dialog */
  onDeployRequested?: (webPlacement: WebPlacement) => void;
  /** Project ID for loading onboardings */
  projectId: string;
  /** User ID for createdBy field */
  userId: string;
  /** Dialog mode: create or edit */
  mode: 'create' | 'edit';
  /** Web Placement to edit (required for edit mode) */
  webPlacement?: WebPlacement;
  /** Check if alias is unique */
  isAliasUnique?: (alias: string, excludeId?: string) => Promise<boolean>;
  /** Pre-filled onboarding selection (from onboarding canvas) */
  initialOnboardingSelection?: OnboardingSelection;
  /** Lock onboarding selection (disable picker when pre-filled from canvas) */
  lockOnboarding?: boolean;
  /** Template placement for "duplicate" flow — pre-fills form in create mode with source settings */
  templateWebPlacement?: WebPlacement;
  /**
   * Article mode (R6b): the placement is backed by an ARTICLE DRAFT, not an
   * onboarding version. Variables and screens for the settings sections
   * (entry rules, bindings, reference validation) come straight from this
   * FlowDocument instead of the onboardings collection — the draft has no
   * real version doc (synthetic versionId 'article-draft').
   */
  articleFlowDocument?: FlowDocument;
  /** Publish-gate "← Step 2 · toggle" (AC-026): scroll to and pulse the Attribution block on open. */
  highlightAttribution?: boolean;
}

/**
 * Form state
 *
 * Architecture: "DEFINE → USE" Pattern
 * - Source parameters (DEFINE): urlParams, serverData, variableInputs, customVariables
 * - Destination mappings (USE): redirectMappings, stripeMappings, webhooks
 */
interface FormState {
  /** Retain published provider settings while the dedicated editor is unfinished. */
  paddleCheckout?: WebPlacementConfig['paddleCheckout'];
  // Basic fields
  name: string;
  webAlias: string;
  description: string;
  onboardingSelection: OnboardingSelection | null;

  // Redirect URL (under description, expanded by default)
  fallbackUrl: string | undefined;
  urlEncode: boolean;
  conditionalRedirect: WebConditionalRedirectConfig | undefined;

  // ==========================================
  // SOURCE PARAMETERS (DEFINE)
  // ==========================================
  /** URL parameters (utm_source, gclid, etc.) */
  urlParams: ParameterDefinition[];
  /** Server-injected data (geoData.country, ip, etc.) */
  serverData: ParameterDefinition[];
  /** Variable inputs from V2 funnel variables */
  variableInputs: ParameterDefinition[];
  /** Custom variables from analytics scripts */
  customVariables: ParameterDefinition[];

  // ==========================================
  // DESTINATION MAPPINGS (USE)
  // ==========================================
  /** Parameters mapped to redirect URL */
  redirectMappings: DestinationParameterMapping[];
  /** Parameters mapped to Stripe metadata */
  stripeMappings: DestinationParameterMapping[];
  /** Stripe metadata configuration */
  stripeConfig: WebStripeMetadataConfig | undefined;
  /** Webhooks configuration */
  webhooks: WebhookConfig[];
  /** Variable bindings: URL params/server data → V2 variables (V2 only) */
  variableBindings: VariableBinding[];
  /** Entry screen rules: URL param conditions for initial screen selection (V2 only) */
  entryScreenRules: EntryScreenRule[];

  // ==========================================
  // STRIPE CHECKOUT EMAIL
  // ==========================================
  /** Stripe checkout email field configuration */
  stripeEmailConfig: WebStripeConfig | undefined;

  // ==========================================
  // ADVANCED
  // ==========================================
  customAnalytics: WebCustomAnalyticsConfig | undefined;
  seo: WebSEOConfig | undefined;

  // ==========================================
  // CONSENT MANAGEMENT (GDPR)
  // ==========================================
  consentManagement: ConsentManagementConfig | undefined;

  // ==========================================
  // USER DATA PERSISTENCE (V2 only)
  // ==========================================
  /** Whether to persist user progress to localStorage (default: true) */
  saveUserData: boolean;
  /** Whether to remember payment method for frictionless upsell (default: true) */
  rememberPaymentMethod: boolean;

  // Legacy (for backwards compatibility during migration)
  /** @deprecated Use separate source arrays */
  parameters: ParameterDefinition[] | undefined;
  /** @deprecated Use fallbackUrl */
  redirectUrl: string | undefined;
  /** @deprecated Use stripeConfig */
  dataCollection: WebDataCollectionConfig | undefined;
}

/**
 * Validation errors
 */
interface ValidationErrors {
  name?: string;
  webAlias?: string;
  description?: string;
  onboarding?: string;
  webhooks?: string;
  references?: string;
}

/**
 * Validate URL format
 */
const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * Format date for display
 */
const formatDate = (isoDate: string): string => {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoDate;
  }
};

/**
 * Bridge component: renders V2 Variable CRUD panel and syncs variable changes
 * to the parent via callback. Must be rendered inside VariableEditorProvider.
 */
const V2VariableCrudWithSync: React.FC<{
  onVariablesChange: (vars: FunnelVariable[]) => void;
  disabled: boolean;
}> = ({ onVariablesChange, disabled }) => {
  const { variables, loading, createVariable, updateVariable, deleteVariable } = useVariableEditor();

  useEffect(() => {
    onVariablesChange(Object.values(variables));
  }, [variables, onVariablesChange]);

  return (
    <V2VariableCrudPanel
      variables={variables}
      loading={loading}
      onCreateVariable={createVariable}
      onUpdateVariable={updateVariable}
      onDeleteVariable={deleteVariable}
      disabled={disabled}
      defaultScope="global"
    />
  );
};

/**
 * Generate a random URL-safe alias (format: xxx-xxxx-xxx, lowercase alphanumeric)
 */
const generateRandomAlias = (): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const segment = (len: number) =>
    Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${segment(3)}-${segment(4)}-${segment(3)}`;
};

const WEBPLACEMENT_STRIPE_METADATA_ANCHOR = GUIDE_ANCHORS.webplacementStripeMetadata;
const WEBPLACEMENT_ENTRY_RULES_ANCHOR = GUIDE_ANCHORS.webplacementEntryRules;

/**
 * WebPlacementDialog Component
 */
export const WebPlacementDialog: React.FC<WebPlacementDialogProps> = ({
  open,
  onClose,
  onCreate,
  onUpdate,
  onDeployRequested,
  projectId,
  userId,
  mode,
  webPlacement,
  isAliasUnique,
  initialOnboardingSelection,
  lockOnboarding = false,
  articleFlowDocument,
  templateWebPlacement,
  highlightAttribution = false,
}) => {
  const theme = useTheme();
  const guideService = useMemo(() => new GuideService(), []);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Load custom domain for project (to display custom domain URL if active)
  const { domain: customDomain } = useCustomDomain(projectId);
  const activeCustomDomain = customDomain?.status === 'active' ? customDomain.domain : null;

  // Form state with new "DEFINE → USE" architecture
  const [formState, setFormState] = useState<FormState>({
    // Basic
    name: '',
    webAlias: '',
    description: '',
    onboardingSelection: null,
    // Redirect URL
    fallbackUrl: undefined,
    urlEncode: false,
    conditionalRedirect: undefined,
    // Source parameters (DEFINE)
    urlParams: [],
    serverData: [],
    variableInputs: [],
    customVariables: [],
    // Destination mappings (USE)
    redirectMappings: [],
    stripeMappings: [],
    stripeConfig: undefined,
    webhooks: [],
    variableBindings: [],
    entryScreenRules: [],
    // Stripe checkout email
    stripeEmailConfig: undefined,
    // Advanced
    customAnalytics: undefined,
    seo: undefined,
    // Consent Management
    consentManagement: undefined,
    // User Data Persistence
    saveUserData: true,
    // Payment Method Persistence (Upsell)
    rememberPaymentMethod: true,
    // Legacy
    parameters: undefined,
    redirectUrl: undefined,
    dataCollection: undefined,
  });

  // Accordion expansion state - sources and destinations sections
  const [expandedSourceSection, setExpandedSourceSection] = useState<string | false>(false);
  const [expandedDestSection, setExpandedDestSection] = useState<string | false>(false);
  const [expandedAdvancedSection, setExpandedAdvancedSection] = useState<string | false>(false);

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [loadingOnboarding, setLoadingOnboarding] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedLocal, setCopiedLocal] = useState(false);
  // Name and alias are independent fields - no auto-generation
  const [aliasCheckLoading, setAliasCheckLoading] = useState(false);
  // V2 variable loading
  const [availableVariables, setAvailableVariables] = useState<FunnelVariable[]>([]);
  const [loadingVariables, setLoadingVariables] = useState(false);
  const [onboardingSchemaStatus, setOnboardingSchemaStatus] = useState<'idle' | 'loading' | 'v2' | 'unsupported' | 'error'>('idle');
  // V2 step nodes for Entry Screen Rules dropdown
  const [v2StepNodes, setV2StepNodes] = useState<Array<{ id: string; name: string }>>([]);

  // Internal mode tracking for "Save without Publish" flow:
  // After first create-save, switches to 'edit' internally to prevent duplicate creates
  const [internalMode, setInternalMode] = useState<'create' | 'edit'>(mode);
  const savedPlacementRef = useRef<WebPlacement | null>(webPlacement ?? null);
  const [saveOnlySuccess, setSaveOnlySuccess] = useState(false);
  // Gate the attribution seed hook until initializeForm has finished writing the form
  // state for THIS open (prevents seeding into stale/default rows during the async setup).
  const [formReady, setFormReady] = useState(false);
  const activePlacement = webPlacement || savedPlacementRef.current;
  const canEditAlias = internalMode === 'create' || activePlacement?.status === 'draft';
  const originalAlias = activePlacement?.alias || '';

  // Compute preview URL (cloud) - uses custom domain if active
  const previewUrl = useMemo(() => {
    if (!formState.webAlias) return '';
    return getFunnelUrl(formState.webAlias, activeCustomDomain);
  }, [formState.webAlias, activeCustomDomain]);

  // Compute local dev URL with Debug Panel
  const localDevUrl = useMemo(() => {
    if (!formState.webAlias) return '';
    return getLocalDevFunnelUrl(formState.webAlias);
  }, [formState.webAlias]);

  // SDK config download URL (GCS assets via Load Balancer)
  const sdkConfigUrl = useMemo(() => {
    if (!formState.webAlias || !projectId) return '';
    return getWebPlacementSdkConfigUrl(projectId, formState.webAlias);
  }, [formState.webAlias, projectId]);

  /**
   * Compute all available parameters from all sources
   * Used by destination sections for parameter selection
   */
  const allAvailableParameters = useMemo((): ParameterDefinition[] => {
    return [
      ...formState.urlParams,
      ...formState.serverData,
      ...formState.variableInputs,
      ...formState.customVariables,
    ];
  }, [formState.urlParams, formState.serverData, formState.variableInputs, formState.customVariables]);

  /**
   * Count of defined parameters by source type (for Accordion badges)
   */
  const parameterCounts = useMemo(() => ({
    url: formState.urlParams.length,
    server: formState.serverData.length,
    variable: formState.variableInputs.length,
    custom: formState.customVariables.length,
    total: allAvailableParameters.length,
  }), [formState.urlParams, formState.serverData, formState.variableInputs, formState.customVariables, allAvailableParameters]);

  /**
   * Load V2 variables and step nodes from the selected onboarding version.
   * Unsupported schemas fail fast; no legacy fallback or subcollection probing runs here.
   */
  const loadV2SourcesFromOnboarding = useCallback(
    async (selection: OnboardingSelection | null) => {
      if (!selection?.onboardingId || !selection?.versionId) {
        setAvailableVariables([]);
        setV2StepNodes([]);
        setOnboardingSchemaStatus('idle');
        return;
      }

      setLoadingVariables(true);
      setOnboardingSchemaStatus('loading');
      try {
        const schemaResult = await detectSchemaVersion(
          projectId,
          selection.onboardingId,
          selection.versionId
        );

        if (schemaResult !== 'v2') {
          setAvailableVariables([]);
          setV2StepNodes([]);
          setOnboardingSchemaStatus(schemaResult);
          setErrors((prev) => ({
            ...prev,
            onboarding: schemaResult === 'unsupported'
              ? 'Selected onboarding version is not publishable by Web Funnels. Migrate it to schemaVersion 2.x first.'
              : 'Unable to verify the selected onboarding version schema.',
          }));
          return;
        }

        const flowVarService = new FlowVariablesService();
        const stepNodeService = new StepNodeScreenService();
        const [varsRecord, stepNodes] = await Promise.all([
          flowVarService.getVariables(projectId, selection.onboardingId, selection.versionId),
          stepNodeService.listScreens(projectId, selection.onboardingId, selection.versionId),
        ]);

        setAvailableVariables(Object.values(varsRecord));
        setV2StepNodes(stepNodes.map(s => ({ id: s.id, name: s.name || s.id })));
        setOnboardingSchemaStatus('v2');
        setErrors((prev) => ({ ...prev, onboarding: undefined }));
      } catch (error) {
        console.error('Failed to load V2 variables/screens:', error);
        setAvailableVariables([]);
        setV2StepNodes([]);
        setOnboardingSchemaStatus('error');
        setErrors((prev) => ({
          ...prev,
          onboarding: 'Failed to load the selected V2 onboarding version.',
        }));
      } finally {
        setLoadingVariables(false);
      }
    },
    [projectId]
  );

  // Load V2 variables and step nodes when onboarding selection changes.
  // Article mode: the draft carries its FlowDocument directly — read screens
  // and variables from it (no onboarding/version docs exist for a draft).
  useEffect(() => {
    if (articleFlowDocument) {
      const screens = (articleFlowDocument.screens ?? {}) as Record<string, { name?: string }>;
      const variables = (articleFlowDocument.variables ?? {}) as Record<string, FunnelVariable>;
      setAvailableVariables(Object.values(variables));
      setV2StepNodes(Object.entries(screens).map(([id, screen]) => ({ id, name: screen?.name || id })));
      setOnboardingSchemaStatus('v2');
      setErrors((prev) => ({ ...prev, onboarding: undefined }));
      return;
    }
    loadV2SourcesFromOnboarding(formState.onboardingSelection);
  }, [articleFlowDocument, formState.onboardingSelection, loadV2SourcesFromOnboarding]);

  // Load onboarding selection from web placement config
  const loadOnboardingSelection = useCallback(
    async (config: WebPlacementConfig) => {
      if (!config.funnelId || !config.versionId) return null;

      setLoadingOnboarding(true);
      try {
        const onboardingService = new OnboardingService();
        const onboarding = await onboardingService.getOnboarding(projectId, config.funnelId);

        if (!onboarding) return null;

        // Find the version
        const versions = await onboardingService.listVersions(projectId, config.funnelId);
        const version = versions.find((v) => v.id === config.versionId);

        if (!version) return null;

        return {
          onboardingId: onboarding.id,
          onboardingName: onboarding.name,
          versionId: version.id,
          versionName: version.name,
          versionCreatedAt: version.createdAt,
        } as OnboardingSelection;
      } catch (error) {
        console.error('Failed to load onboarding selection:', error);
        return null;
      } finally {
        setLoadingOnboarding(false);
      }
    },
    [projectId]
  );

  /**
   * Helper: Split legacy parameters array into source-specific arrays
   */
  const splitParametersBySource = useCallback((params: ParameterDefinition[] | undefined): {
    urlParams: ParameterDefinition[];
    serverData: ParameterDefinition[];
    variableInputs: ParameterDefinition[];
    customVariables: ParameterDefinition[];
  } => {
    if (!params || params.length === 0) {
      return { urlParams: [], serverData: [], variableInputs: [], customVariables: [] };
    }

    return {
      urlParams: params.filter(p => p.source === 'url'),
      serverData: params.filter(p => p.source === 'server'),
      variableInputs: params.filter(p => p.source === 'variable'),
      customVariables: params.filter(p => p.source === 'custom'),
    };
  }, []);

  // Initialize form state when dialog opens or web placement changes
  useEffect(() => {
    if (!open) return;

    const initializeForm = async () => {
      setFormReady(false);
      if (mode === 'edit' && webPlacement) {
        // Load existing values
        let onboardingSelection: OnboardingSelection | null = null;

        // Use initialOnboardingSelection if provided (from canvas), otherwise load from config
        if (initialOnboardingSelection) {
          onboardingSelection = initialOnboardingSelection;
        } else if (webPlacement.config?.funnelId && webPlacement.config?.versionId) {
          onboardingSelection = await loadOnboardingSelection(webPlacement.config);
        }

        // Split legacy parameters into source-specific arrays
        const splitParams = splitParametersBySource(webPlacement.config?.parameters);

        setFormState({
          // Basic
          name: webPlacement.name || '',
          webAlias: webPlacement.alias || '',
          description: webPlacement.description || '',
          onboardingSelection,
          // Redirect URL
          fallbackUrl: webPlacement.config?.fallbackUrl || webPlacement.config?.redirectUrl,
          urlEncode: webPlacement.config?.urlEncode ?? false,
          conditionalRedirect: webPlacement.config?.conditionalRedirect,
          // Source parameters (DEFINE) - from new or legacy format
          urlParams: webPlacement.config?.urlParams || splitParams.urlParams,
          serverData: webPlacement.config?.serverData || splitParams.serverData,
          variableInputs: webPlacement.config?.variableInputs || splitParams.variableInputs,
          customVariables: webPlacement.config?.customVariables || splitParams.customVariables,
          // Destination mappings (USE)
          redirectMappings: webPlacement.config?.redirectMappings || [],
          stripeMappings: webPlacement.config?.stripeMappings || [],
          stripeConfig: webPlacement.config?.dataCollection?.stripeMetadata,
          webhooks: webPlacement.config?.dataCollection?.webhooks || [],
          variableBindings: webPlacement.config?.variableBindings || [],
          entryScreenRules: webPlacement.config?.entryScreenRules || [],
          // Stripe checkout email
          stripeEmailConfig: webPlacement.config?.stripe,
          paddleCheckout: webPlacement.config?.paddleCheckout,
          // Advanced
          customAnalytics: webPlacement.config?.customAnalytics,
          seo: webPlacement.config?.seo,
          // Consent Management
          consentManagement: webPlacement.config?.consentManagement,
          // User Data Persistence
          saveUserData: webPlacement.config?.saveUserData ?? true,
          // Payment Method Persistence (Upsell)
          rememberPaymentMethod: webPlacement.config?.rememberPaymentMethod ?? true,
          // Legacy (for backwards compatibility)
          parameters: webPlacement.config?.parameters,
          redirectUrl: webPlacement.config?.redirectUrl,
          dataCollection: webPlacement.config?.dataCollection,
        });
      } else if (templateWebPlacement) {
        // Create mode with template (duplicate flow) — pre-fill from source, clear onboarding fields
        const tplConfig = templateWebPlacement.config;
        const splitParams = splitParametersBySource(tplConfig?.parameters);

        // Filter mappings — remove entries referencing cleared onboarding variables.
        const clearedSourceTypes = new Set(['variable']);
        const filterMappings = <T extends { sourceType: string }>(mappings: T[] | undefined): T[] =>
          (mappings || []).filter(m => !clearedSourceTypes.has(m.sourceType));

        setFormState({
          // Basic — use template name with "(Copy)", suggest alias with "-copy"
          name: `${templateWebPlacement.name} (Copy)`,
          webAlias: `${templateWebPlacement.alias}-copy`,
          description: templateWebPlacement.description || '',
          onboardingSelection: initialOnboardingSelection || null,
          // Redirect URL — keep
          fallbackUrl: tplConfig?.fallbackUrl || tplConfig?.redirectUrl,
          urlEncode: tplConfig?.urlEncode ?? false,
          conditionalRedirect: tplConfig?.conditionalRedirect,
          // Source parameters (DEFINE) — keep onboarding-independent, clear onboarding-dependent
          urlParams: tplConfig?.urlParams || splitParams.urlParams,
          serverData: tplConfig?.serverData || splitParams.serverData,
          variableInputs: [], // cleared — references source onboarding variables
          customVariables: tplConfig?.customVariables || splitParams.customVariables,
          // Destination mappings (USE) — filter out onboarding variable sourceType entries
          redirectMappings: filterMappings(tplConfig?.redirectMappings),
          stripeMappings: filterMappings(tplConfig?.stripeMappings),
          stripeConfig: tplConfig?.dataCollection?.stripeMetadata,
          webhooks: tplConfig?.dataCollection?.webhooks || [],
          variableBindings: [], // cleared — references source onboarding variables
          entryScreenRules: [], // cleared — references source onboarding screens
          // Stripe checkout email — clear onboarding refs
          stripeEmailConfig: undefined,
          paddleCheckout: tplConfig?.paddleCheckout,
          // Advanced — keep
          customAnalytics: tplConfig?.customAnalytics,
          seo: tplConfig?.seo,
          // Consent Management — keep
          consentManagement: tplConfig?.consentManagement,
          // User Data Persistence — keep
          saveUserData: tplConfig?.saveUserData ?? true,
          // Payment Method Persistence — keep
          rememberPaymentMethod: tplConfig?.rememberPaymentMethod ?? true,
          // Legacy
          parameters: undefined,
          redirectUrl: undefined,
          dataCollection: tplConfig?.dataCollection,
        });
      } else {
        // Reset for create mode (use initialOnboardingSelection if provided)
        setFormState({
          // Basic — auto-fill name from onboarding when opened from canvas
          name: initialOnboardingSelection?.onboardingName || '',
          webAlias: '',
          description: '',
          onboardingSelection: initialOnboardingSelection || null,
          // Redirect URL
          fallbackUrl: undefined,
          urlEncode: false,
          conditionalRedirect: undefined,
          // Source parameters (DEFINE)
          urlParams: [],
          serverData: [],
          variableInputs: [],
          customVariables: [],
          // Destination mappings (USE)
          redirectMappings: [],
          stripeMappings: [],
          stripeConfig: undefined,
          webhooks: [],
          variableBindings: [],
          entryScreenRules: [],
          // Stripe checkout email
          stripeEmailConfig: undefined,
          // Advanced
          customAnalytics: undefined,
          seo: undefined,
          // Consent Management
          consentManagement: undefined,
          // User Data Persistence
          saveUserData: true,
          // Payment Method Persistence (Upsell)
          rememberPaymentMethod: true,
          // Legacy
          parameters: undefined,
          redirectUrl: undefined,
          dataCollection: undefined,
        });
        setExpandedSourceSection(false);
        setExpandedDestSection(false);
        setExpandedAdvancedSection(false);
      }
      setErrors({});
      setSubmitError(null);
      setCopied(false);
      setInternalMode(mode);
      savedPlacementRef.current = webPlacement ?? null;
      setSaveOnlySuccess(false);
      setFormReady(true);
    };

    initializeForm();
  }, [open, mode, webPlacement, loadOnboardingSelection, splitParametersBySource, initialOnboardingSelection, templateWebPlacement]);

  const protectedKeys = useMemo(
    () => collectProtectedKeys({ entryScreenRules: formState.entryScreenRules, variableBindings: formState.variableBindings }),
    [formState.entryScreenRules, formState.variableBindings],
  );

  // DC-20: seed the toggles ONCE on create from integrations that are enabled AND configured.
  const handleAttributionSeed = useCallback((toggles: AttributionToggleState) => {
    setFormState((prev) => {
      const next = applyToggles(
        { urlParams: prev.urlParams, serverData: prev.serverData, stripeMappings: prev.stripeMappings },
        toggles,
      );
      return { ...prev, urlParams: next.urlParams ?? [], serverData: next.serverData ?? [], stripeMappings: next.stripeMappings ?? [] };
    });
  }, []);
  const { markTouched: markAttributionTouched } = useAttributionSeed({
    open: open && formReady,
    mode,
    hasTemplate: Boolean(templateWebPlacement),
    projectId,
    onSeed: handleAttributionSeed,
  });

  const handleAttributionChange = useCallback((next: AttributionConfigSlice, _key: AttributionPresetKey, _on: boolean) => {
    markAttributionTouched();
    setFormState((prev) => ({
      ...prev,
      urlParams: next.urlParams ?? [],
      serverData: next.serverData ?? [],
      stripeMappings: next.stripeMappings ?? [],
    }));
  }, [markAttributionTouched]);

  // Focus name field when dialog opens in create mode
  useEffect(() => {
    if (open && mode === 'create') {
      const timer = setTimeout(() => {
        const input = nameInputRef.current;
        if (!input) return;
        const active = input.ownerDocument.activeElement;
        // Initial focus must not steal typing from an already selected control.
        if (!active || active === input.ownerDocument.body || active === input.closest('[role="dialog"]')) {
          input.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, mode]);

  /**
   * Validate form
   */
  const validate = useCallback(async (): Promise<boolean> => {
    const newErrors: ValidationErrors = {};

    // Name validation
    if (!formState.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formState.name.length > WEB_PLACEMENT_VALIDATION.NAME_MAX_LENGTH) {
      newErrors.name = `Name must be at most ${WEB_PLACEMENT_VALIDATION.NAME_MAX_LENGTH} characters`;
    }

    // Web alias validation
    if (!formState.webAlias.trim()) {
      newErrors.webAlias = 'Web alias is required';
    } else if (!isValidWebAlias(formState.webAlias)) {
      if (formState.webAlias.length < WEB_PLACEMENT_VALIDATION.ALIAS_MIN_LENGTH) {
        newErrors.webAlias = `Alias must be at least ${WEB_PLACEMENT_VALIDATION.ALIAS_MIN_LENGTH} characters`;
      } else if (formState.webAlias.length > WEB_PLACEMENT_VALIDATION.ALIAS_MAX_LENGTH) {
        newErrors.webAlias = `Alias must be at most ${WEB_PLACEMENT_VALIDATION.ALIAS_MAX_LENGTH} characters`;
      } else if (!WEB_PLACEMENT_VALIDATION.ALIAS_PATTERN.test(formState.webAlias)) {
        newErrors.webAlias = 'Alias can only contain letters, numbers, and hyphens';
      } else if (
        WEB_PLACEMENT_VALIDATION.RESERVED_ALIASES.includes(
          formState.webAlias as (typeof WEB_PLACEMENT_VALIDATION.RESERVED_ALIASES)[number]
        )
      ) {
        newErrors.webAlias = 'This alias is reserved';
      }
    } else if (
      isAliasUnique &&
      (internalMode === 'create' || (canEditAlias && formState.webAlias.trim() !== originalAlias))
    ) {
      // In create mode there is no existing placement to exclude. In draft edit
      // mode, exclude the current placement so an unchanged alias does not
      // self-collide.
      setAliasCheckLoading(true);
      try {
        const isUnique = await isAliasUnique(
          formState.webAlias,
          internalMode === 'edit' ? activePlacement?.id : undefined
        );
        if (!isUnique) {
          newErrors.webAlias = 'This alias is already in use';
        }
      } catch (e) {
        // Show the actual error (e.g., missing Firestore index)
        const message = e instanceof Error ? e.message : 'Failed to check alias';
        newErrors.webAlias = message;
      } finally {
        setAliasCheckLoading(false);
      }
    }

    // Description validation
    if (
      formState.description &&
      formState.description.length > WEB_PLACEMENT_VALIDATION.DESCRIPTION_MAX_LENGTH
    ) {
      newErrors.description = `Description must be at most ${WEB_PLACEMENT_VALIDATION.DESCRIPTION_MAX_LENGTH} characters`;
    }

    // Onboarding validation
    if (!formState.onboardingSelection) {
      newErrors.onboarding = 'Please select an onboarding';
    } else if (onboardingSchemaStatus !== 'v2') {
      newErrors.onboarding = onboardingSchemaStatus === 'unsupported'
        ? 'Selected onboarding version is not publishable by Web Funnels. Migrate it to schemaVersion 2.x first.'
        : 'Selected onboarding version must be verified as schemaVersion 2.x before saving.';
    } else if (v2StepNodes.length === 0) {
      newErrors.onboarding = 'Selected V2 onboarding version has no screensV2 data.';
    }

    if (formState.onboardingSelection && onboardingSchemaStatus === 'v2') {
      const referenceIssues = validateWebPlacementReferences({
        config: {
          funnelId: formState.onboardingSelection.onboardingId,
          versionId: formState.onboardingSelection.versionId,
          urlParams: formState.urlParams,
          variableInputs: formState.variableInputs,
          variableBindings: formState.variableBindings,
          entryScreenRules: formState.entryScreenRules,
          stripe: formState.stripeEmailConfig,
        },
        variables: availableVariables,
        screens: v2StepNodes,
      });

      if (referenceIssues.length > 0) {
        newErrors.references = formatWebPlacementReferenceIssues(referenceIssues);
      }
    }

    // Webhook URL validation
    const invalidWebhooks = formState.webhooks?.filter((w) => !w.url || !isValidUrl(w.url));
    if (invalidWebhooks && invalidWebhooks.length > 0) {
      const names = invalidWebhooks.map((w) => w.name || 'Unnamed').join(', ');
      newErrors.webhooks = `Invalid webhook URL: ${names}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    formState,
    internalMode,
    isAliasUnique,
    onboardingSchemaStatus,
    v2StepNodes,
    availableVariables,
    canEditAlias,
    originalAlias,
    activePlacement?.id,
  ]);

  /**
   * Handle name field change
   */
  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newName = event.target.value;
      setFormState((prev) => ({
        ...prev,
        name: newName,
      }));

      // Clear error
      if (errors.name) {
        setErrors((prev) => ({ ...prev, name: undefined }));
      }
    },
    [errors.name]
  );

  /**
   * Handle alias field change
   */
  const handleAliasChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
      setFormState((prev) => ({
        ...prev,
        webAlias: value,
      }));

      if (errors.webAlias) {
        setErrors((prev) => ({ ...prev, webAlias: undefined }));
      }
    },
    [errors.webAlias]
  );

  /**
   * Generate a random alias and set it
   */
  const handleGenerateAlias = useCallback(() => {
    const alias = generateRandomAlias();
    setFormState((prev) => ({ ...prev, webAlias: alias }));
    if (errors.webAlias) {
      setErrors((prev) => ({ ...prev, webAlias: undefined }));
    }
  }, [errors.webAlias]);

  /**
   * Handle description field change
   */
  const handleDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({
        ...prev,
        description: event.target.value,
      }));

      if (errors.description) {
        setErrors((prev) => ({ ...prev, description: undefined }));
      }
    },
    [errors.description]
  );

  /**
   * Handle onboarding selection change
   */
  const handleOnboardingChange = useCallback((selection: OnboardingSelection | null) => {
    setFormState((prev) => ({
      ...prev,
      onboardingSelection: selection,
    }));
    setErrors((prev) => ({
      ...prev,
      onboarding: undefined,
      references: undefined,
    }));
  }, []);

  /**
   * Sync V2 variables from VariableEditorProvider to local state.
   * Called by V2VariableCrudWithSync when variables change in the context.
   */
  const handleVariablesChange = useCallback((vars: FunnelVariable[]) => {
    setAvailableVariables(vars);
  }, []);

  /**
   * Copy URL to clipboard
   */
  const handleCopyUrl = useCallback(async () => {
    if (!previewUrl) return;

    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  }, [previewUrl]);

  /**
   * Copy local dev URL to clipboard
   */
  const handleCopyLocalUrl = useCallback(async () => {
    if (!localDevUrl) return;

    try {
      await navigator.clipboard.writeText(localDevUrl);
      setCopiedLocal(true);
      setTimeout(() => setCopiedLocal(false), 2000);
    } catch (error) {
      console.error('Failed to copy local URL:', error);
    }
  }, [localDevUrl]);

  /**
   * Core save logic — builds config and persists to Firestore.
   * Returns the saved WebPlacement on success, or null on failure.
   */
  const performSave = useCallback(async (): Promise<WebPlacement | null> => {
    const isValid = await validate();
    if (!isValid) return null;

    setLoading(true);
    setSubmitError(null);
    setSaveOnlySuccess(false);

    try {
      const browserWebhooks = formState.webhooks
        .filter((webhook) => webhook.triggerEvent !== 'screen_enter_await')
        .map((webhook) => {
          const sanitizedWebhook: WebhookConfig = { ...webhook };
          delete sanitizedWebhook.bearerToken;
          delete sanitizedWebhook.headers;
          return sanitizedWebhook;
        });

      // Build config using new "DEFINE → USE" architecture
      const config: WebPlacementConfig = {
        // Core onboarding reference
        funnelId: formState.onboardingSelection!.onboardingId,
        versionId: formState.onboardingSelection!.versionId,
        entityName: `${formState.onboardingSelection!.onboardingName} / ${formState.onboardingSelection!.versionName}`,
        funnelName: formState.onboardingSelection!.onboardingName,
        versionName: formState.onboardingSelection!.versionName,

        // Redirect URL configuration
        ...(formState.fallbackUrl && { fallbackUrl: formState.fallbackUrl }),
        urlEncode: formState.urlEncode,
        ...(formState.conditionalRedirect && { conditionalRedirect: formState.conditionalRedirect }),

        // ==========================================
        // SOURCE PARAMETERS (DEFINE)
        // ==========================================
        ...(formState.urlParams.length > 0 && { urlParams: formState.urlParams }),
        ...(formState.serverData.length > 0 && { serverData: formState.serverData }),
        ...(formState.variableInputs.length > 0 && { variableInputs: formState.variableInputs }),
        ...(formState.customVariables.length > 0 && { customVariables: formState.customVariables }),

        // Legacy: Also save as unified parameters array for backwards compatibility
        ...(allAvailableParameters.length > 0 && { parameters: allAvailableParameters }),

        // ==========================================
        // DESTINATION MAPPINGS (USE)
        // ==========================================
        ...(formState.redirectMappings.length > 0 && { redirectMappings: formState.redirectMappings }),
        ...(formState.stripeMappings.length > 0 && { stripeMappings: formState.stripeMappings }),

        // Variable bindings (V2 only)
        ...(formState.variableBindings.length > 0 && { variableBindings: formState.variableBindings }),

        // Entry screen rules (V2 only)
        ...(formState.entryScreenRules.length > 0 && { entryScreenRules: formState.entryScreenRules }),

        // Data collection (webhooks & Stripe metadata config)
        ...((browserWebhooks.length > 0 || formState.stripeConfig) && {
          dataCollection: {
            ...(formState.stripeConfig && { stripeMetadata: formState.stripeConfig }),
            ...(browserWebhooks.length > 0 && { webhooks: browserWebhooks }),
          },
        }),

        // ==========================================
        // STRIPE CHECKOUT EMAIL
        // ==========================================
        ...(formState.stripeEmailConfig && { stripe: formState.stripeEmailConfig }),
        ...(formState.paddleCheckout !== undefined && { paddleCheckout: formState.paddleCheckout }),

        // ==========================================
        // ADVANCED
        // ==========================================
        ...(formState.customAnalytics && { customAnalytics: formState.customAnalytics }),
        ...(formState.seo && { seo: formState.seo }),

        // ==========================================
        // CONSENT MANAGEMENT
        // ==========================================
        ...(formState.consentManagement && { consentManagement: formState.consentManagement }),

        // ==========================================
        // USER DATA PERSISTENCE (V2 only)
        // ==========================================
        ...(formState.saveUserData === false && { saveUserData: false }),

        // ==========================================
        // PAYMENT METHOD PERSISTENCE (V2 only, Upsell)
        // ==========================================
        ...(formState.rememberPaymentMethod === false && { rememberPaymentMethod: false }),
      };

      let savedWebPlacement: WebPlacement;

      if (internalMode === 'create' && onCreate) {
        savedWebPlacement = await onCreate({
          name: formState.name.trim(),
          alias: formState.webAlias.trim(),
          // Only include description if it has a value (Firestore rejects undefined)
          ...(formState.description.trim() && { description: formState.description.trim() }),
          config,
        });
        // After first create, switch to edit mode internally to prevent duplicate creates
        setInternalMode('edit');
        savedPlacementRef.current = savedWebPlacement;
      } else if (internalMode === 'edit' && onUpdate && (webPlacement || savedPlacementRef.current)) {
        const placementId = (webPlacement || savedPlacementRef.current)!.id;
        const updateData: UpdateWebPlacementInput = {
          name: formState.name.trim(),
          ...(canEditAlias && formState.webAlias.trim() !== originalAlias && {
            alias: formState.webAlias.trim(),
          }),
          // Only include description if it has a value (Firestore rejects undefined)
          ...(formState.description.trim() && { description: formState.description.trim() }),
          config,
        };
        savedWebPlacement = await onUpdate(placementId, updateData);
        savedPlacementRef.current = savedWebPlacement;
      } else {
        throw new Error('Invalid dialog configuration');
      }

      return savedWebPlacement;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : internalMode === 'create'
            ? 'Failed to create web placement'
            : 'Failed to update web placement';
      setSubmitError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [
    formState,
    validate,
    onCreate,
    onUpdate,
    internalMode,
    webPlacement,
    allAvailableParameters,
    canEditAlias,
    originalAlias,
  ]);

  /**
   * Handle save & publish (original behavior)
   */
  const handleSave = useCallback(async () => {
    const saved = await performSave();
    if (saved) {
      onDeployRequested?.(saved);
    }
  }, [performSave, onDeployRequested]);

  /**
   * Handle save only (without publishing) — saves to Firestore and stays in dialog
   */
  const handleSaveOnly = useCallback(async () => {
    const saved = await performSave();
    if (saved) {
      setSaveOnlySuccess(true);
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSaveOnlySuccess(false), 3000);
    }
  }, [performSave]);

  // Check if form is valid for enabling Save button
  const canSave =
    formState.name.trim().length > 0 &&
    formState.webAlias.trim().length >= WEB_PLACEMENT_VALIDATION.ALIAS_MIN_LENGTH &&
    formState.onboardingSelection !== null &&
    onboardingSchemaStatus === 'v2' &&
    v2StepNodes.length > 0;

  const dialogTitle = internalMode === 'create' ? 'Create Web Funnel' : 'Edit Web Funnel';

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      maxWidth="sm"
      fullWidth
      data-testid="wp-dialog"
      primaryAction={{
        label: internalMode === 'create' ? 'Create & Publish' : 'Save & Publish',
        onClick: handleSave,
        loading: loading || aliasCheckLoading,
        disabled: !canSave || loadingOnboarding,
        'data-testid': 'wp-dialog-save-btn',
      }}
      secondaryAction={{
        label: 'Save',
        onClick: handleSaveOnly,
        loading: loading || aliasCheckLoading,
        disabled: !canSave || loadingOnboarding,
        'data-testid': 'wp-dialog-save-only-btn',
        startIcon: <SaveIcon />,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(3),
        }}
      >
        {/* Save Only Success */}
        {saveOnlySuccess && (
          <Alert severity="success" onClose={() => setSaveOnlySuccess(false)}>
            Web placement saved successfully. You can continue editing or publish when ready.
          </Alert>
        )}

        {/* Submit Error */}
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Edit mode: Show ID and metadata */}
        {mode === 'edit' && webPlacement && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              p: theme.spacing(2),
              bgcolor: theme.palette.action.hover,
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {webPlacement.id}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={webPlacement.status === 'published' ? 'Published' : 'Draft'}
                size="small"
                color={webPlacement.status === 'published' ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
            {webPlacement.createdAt && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(webPlacement.createdAt)}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Name Field */}
        <TextField
          data-testid="wp-name-input"
          inputRef={nameInputRef}
          label="Name"
          value={formState.name}
          onChange={handleNameChange}
          error={Boolean(errors.name)}
          helperText={errors.name || 'Human-readable name for this web funnel'}
          fullWidth
          required
          disabled={loading}
        />

        {/* Web Alias Field - editable before publish, with Generate button for draft aliases */}
        <TextField
          data-testid="wp-alias-input"
          label="Web Alias"
          value={formState.webAlias}
          onChange={handleAliasChange}
          error={Boolean(errors.webAlias)}
          helperText={
            errors.webAlias ||
            (!canEditAlias
              ? 'Alias cannot be changed after publish'
              : 'URL-friendly identifier (letters, numbers, hyphens, underscores)')
          }
          fullWidth
          required
          disabled={loading || !canEditAlias}
          slotProps={{
            input: {
              endAdornment:
                canEditAlias && !loading ? (
                  <InputAdornment position="end">
                    <Tooltip title="Generate random alias">
                      <IconButton
                        onClick={handleGenerateAlias}
                        edge="end"
                        size="small"
                        data-testid="wp-generate-alias-btn"
                      >
                        <CasinoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
            },
          }}
        />

        {/* Preview URLs — only show for previously published placements (edit mode from props) */}
        {mode === 'edit' && formState.webAlias && isValidWebAlias(formState.webAlias) && (
          <Box
            sx={{
              p: theme.spacing(2),
              bgcolor: theme.palette.success.main + '10',
              borderRadius: 1,
              border: `1px solid ${theme.palette.success.main}30`,
            }}
          >
            {/* Local Dev URL */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                <HomeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle', color: 'warning.main' }} />
                Local Dev (with Debug Panel)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link
                  href={localDevUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'monospace', fontSize: '0.875rem', flexGrow: 1, color: 'warning.main' }}
                >
                  {localDevUrl}
                </Link>
                <Tooltip title={copiedLocal ? 'Copied!' : 'Copy URL'}>
                  <IconButton size="small" onClick={handleCopyLocalUrl}>
                    {copiedLocal ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {/* Cloud URL */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                <CloudIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle', color: 'info.main' }} />
                Cloud URL
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'monospace', fontSize: '0.875rem', flexGrow: 1 }}
                >
                  {previewUrl}
                </Link>
                <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                  <IconButton size="small" onClick={handleCopyUrl}>
                    {copied ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* SDK Config Download */}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                <PhoneIphoneIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle', color: 'secondary.main' }} />
                iOS/Android SDK Config
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem', flexGrow: 1, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  funnel_{formState.webAlias}.json
                </Typography>
                <Tooltip title="Download SDK config (publish-format JSON with all baked data)">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        const response = await fetch(sdkConfigUrl);
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `funnel_${formState.webAlias}.json`;
                        link.click();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error('Failed to download SDK config:', err);
                      }
                    }}
                  >
                    <DownloadIcon fontSize="small" color="secondary" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        )}

        {/* ================================================================= */}
        {/* REDIRECT URL SECTION - Under web alias */}
        {/* ================================================================= */}
        <RedirectUrlSection
          fallbackUrl={formState.fallbackUrl}
          urlEncode={formState.urlEncode}
          conditionalRedirect={formState.conditionalRedirect}
          availableParameters={allAvailableParameters}
          onFallbackUrlChange={(fallbackUrl) => setFormState((prev) => ({ ...prev, fallbackUrl }))}
          onUrlEncodeChange={(urlEncode) => setFormState((prev) => ({ ...prev, urlEncode }))}
          onConditionalRedirectChange={(conditionalRedirect) =>
            setFormState((prev) => ({ ...prev, conditionalRedirect }))
          }
          disabled={loading}
        />

        {/* Onboarding Selection */}
        <Box>
          <Box sx={{ mb: theme.spacing(1) }}>
            <Box component="span" sx={{ fontWeight: 500 }}>
              Onboarding *
            </Box>
          </Box>
          <OnboardingPickerButton
            selection={formState.onboardingSelection}
            onSelectionChange={handleOnboardingChange}
            projectId={projectId}
            disabled={loading || lockOnboarding}
            loading={loadingOnboarding}
          />
          {lockOnboarding && formState.onboardingSelection && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Onboarding is pre-selected from the canvas editor
            </Typography>
          )}
          {errors.onboarding && (
            <Box
              sx={{
                color: theme.palette.error.main,
                fontSize: '0.75rem',
                mt: theme.spacing(0.5),
              }}
            >
              {errors.onboarding}
            </Box>
          )}
          {errors.references && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="wp-reference-validation-error">
              {errors.references}
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ================================================================= */}
        {/* PARAMETER DEFINITIONS (DEFINE) - Separate Accordions by Source */}
        {/* ================================================================= */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            Parameter Definitions
            {parameterCounts.total > 0 && (
              <Chip
                label={`${parameterCounts.total} defined`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Define parameters from different sources to use in destinations
          </Typography>

          <AttributionTogglesSection
            config={{ urlParams: formState.urlParams, serverData: formState.serverData, stripeMappings: formState.stripeMappings }}
            protectedKeys={protectedKeys}
            entryScreenRules={formState.entryScreenRules}
            variableBindings={formState.variableBindings}
            onChange={handleAttributionChange}
            disabled={loading}
            highlight={highlightAttribution}
            onBudgetExceeded={setSubmitError}
          />

          {/* URL Parameters */}
          <Accordion
            data-testid="wp-section-url-params"
            expanded={expandedSourceSection === 'url'}
            onChange={(_, expanded) => setExpandedSourceSection(expanded ? 'url' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                <Typography>URL Parameters</Typography>
                {parameterCounts.url > 0 && (
                  <Chip label={parameterCounts.url} size="small" color="info" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <UrlParametersSection
                parameters={formState.urlParams}
                onChange={(urlParams) => setFormState((prev) => ({ ...prev, urlParams }))}
                disabled={loading}
              />
            </AccordionDetails>
          </Accordion>

          {/* Server Data */}
          <Accordion
            data-testid="wp-section-server-data"
            expanded={expandedSourceSection === 'server'}
            onChange={(_, expanded) => setExpandedSourceSection(expanded ? 'server' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                <Typography>Server Data</Typography>
                {parameterCounts.server > 0 && (
                  <Chip label={parameterCounts.server} size="small" color="secondary" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <ServerDataSection
                parameters={formState.serverData}
                onChange={(serverData) => setFormState((prev) => ({ ...prev, serverData }))}
                disabled={loading}
              />
            </AccordionDetails>
          </Accordion>

          {/* Variable Inputs */}
          <Accordion
            data-testid="wp-section-variable-inputs"
            expanded={expandedSourceSection === 'variable'}
            onChange={(_, expanded) => setExpandedSourceSection(expanded ? 'variable' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TuneIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                <Typography>Variable Inputs</Typography>
                {parameterCounts.variable > 0 && (
                  <Chip label={parameterCounts.variable} size="small" color="info" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <VariableInputsSection
                parameters={formState.variableInputs}
                onChange={(variableInputs) => setFormState((prev) => ({ ...prev, variableInputs }))}
                availableVariables={availableVariables}
                disabled={loading || loadingVariables || onboardingSchemaStatus !== 'v2'}
              />
            </AccordionDetails>
          </Accordion>

          {/* V2 Variable CRUD */}
          {formState.onboardingSelection && onboardingSchemaStatus === 'v2' && (
            <VariableEditorProvider
              projectId={projectId}
              onboardingId={formState.onboardingSelection.onboardingId}
              versionId={formState.onboardingSelection.versionId}
            >
              <Accordion
                data-testid="wp-section-v2-variables"
                expanded={expandedSourceSection === 'v2variables'}
                onChange={(_, expanded) => setExpandedSourceSection(expanded ? 'v2variables' : false)}
                sx={{ '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TuneIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                    <Typography>V2 Variables</Typography>
                    {availableVariables.length > 0 && (
                      <Chip label={availableVariables.length} size="small" color="secondary" variant="outlined" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <V2VariableCrudWithSync
                    onVariablesChange={handleVariablesChange}
                    disabled={loading}
                  />
                </AccordionDetails>
              </Accordion>
            </VariableEditorProvider>
          )}

          {/* Custom Variables */}
          <Accordion
            data-testid="wp-section-custom-variables"
            expanded={expandedSourceSection === 'custom'}
            onChange={(_, expanded) => setExpandedSourceSection(expanded ? 'custom' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                <Typography>Custom Variables</Typography>
                {parameterCounts.custom > 0 && (
                  <Chip label={parameterCounts.custom} size="small" color="warning" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <CustomVariablesSection
                parameters={formState.customVariables}
                onChange={(customVariables) => setFormState((prev) => ({ ...prev, customVariables }))}
                disabled={loading}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ================================================================= */}
        {/* DESTINATIONS (USE) - Where to send parameters */}
        {/* ================================================================= */}
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Destinations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configure where to send the defined parameters
            </Typography>
          </Box>

          {/* Redirect URL Parameters */}
          <Accordion
            data-testid="wp-section-redirect-params"
            expanded={expandedDestSection === 'redirect'}
            onChange={(_, expanded) => setExpandedDestSection(expanded ? 'redirect' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Redirect URL Parameters</Typography>
                {formState.redirectMappings.length > 0 && (
                  <Chip
                    label={`${formState.redirectMappings.length} params`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <RedirectParametersSection
                availableParameters={allAvailableParameters}
                mappings={formState.redirectMappings}
                onChange={(redirectMappings) => setFormState((prev) => ({ ...prev, redirectMappings }))}
                redirectUrl={formState.fallbackUrl}
                urlEncode={formState.urlEncode}
                disabled={loading}
              />
            </AccordionDetails>
          </Accordion>

          {/* Stripe Metadata */}
          <Accordion
            data-testid="wp-section-stripe-metadata"
            expanded={expandedDestSection === 'stripe'}
            onChange={(_, expanded) => setExpandedDestSection(expanded ? 'stripe' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WithGuideHelp
                  guideKey={WEBPLACEMENT_STRIPE_METADATA_ANCHOR.guideKey}
                  guideService={guideService}
                  data-testid={WEBPLACEMENT_STRIPE_METADATA_ANCHOR.testId}
                  position="inline-end"
                  stopClickPropagation
                  iconComponent="span"
                >
                  <Typography>Stripe Metadata</Typography>
                </WithGuideHelp>
                {formState.stripeMappings.length > 0 && (
                  <Chip
                    label={`${formState.stripeMappings.length} params`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <StripeMetadataSection
                availableParameters={allAvailableParameters}
                mappings={formState.stripeMappings}
                onMappingsChange={(stripeMappings) => setFormState((prev) => ({ ...prev, stripeMappings }))}
                disabled={loading}
              />
            </AccordionDetails>
          </Accordion>

          {/* Stripe Email Settings */}
          <Accordion
            data-testid="wp-section-stripe-email"
            expanded={expandedDestSection === 'stripeEmail'}
            onChange={(_, expanded) => setExpandedDestSection(expanded ? 'stripeEmail' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                <Typography>Stripe Email Settings</Typography>
                {formState.stripeEmailConfig?.emailVariableId && (
                  <Chip
                    label="Configured"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, bgcolor: 'info.main', borderRadius: 1, opacity: 0.1 }}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: 'info.main', mt: 0.25 }} />
                  <Typography variant="body2" color="text.secondary">
                    Select the variable that collects the customer email. This email will be pre-filled in Stripe checkout.
                  </Typography>
                </Box>

                <FormControl fullWidth size="small" disabled={loading || loadingVariables || onboardingSchemaStatus !== 'v2'}>
                  <InputLabel id="stripe-email-variable-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon fontSize="small" />
                      Email Variable
                    </Box>
                  </InputLabel>
                  <Select
                    data-testid="wp-stripe-email-select"
                    labelId="stripe-email-variable-label"
                    value={formState.stripeEmailConfig?.emailVariableId || ''}
                    label="Email Variable"
                    onChange={(e) => {
                      const variableId = e.target.value as string;
                      setFormState((prev) => ({
                        ...prev,
                        stripeEmailConfig: variableId
                          ? { emailVariableId: variableId }
                          : undefined,
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>None (no pre-fill)</em>
                    </MenuItem>
                    {availableVariables.filter(v => v.type === 'string').length === 0 && !loadingVariables && (
                      <MenuItem value="" disabled>
                        <em>No string variables found in funnel</em>
                      </MenuItem>
                    )}
                    {availableVariables
                      .filter(v => v.type === 'string')
                      .map((variable) => (
                        <MenuItem key={variable.id} value={variable.id}>
                          {variable.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {formState.stripeEmailConfig?.emailVariableId && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Email from variable <strong>{availableVariables.find(v => v.id === formState.stripeEmailConfig?.emailVariableId)?.name || formState.stripeEmailConfig.emailVariableId}</strong> will be pre-filled in Stripe checkout.
                  </Alert>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Entry Screen Rules */}
          {onboardingSchemaStatus === 'v2' && (
            <Accordion
              data-testid="wp-accordion-entry-screen-rules"
              expanded={expandedDestSection === 'entryScreenRules'}
              onChange={(_, expanded) => setExpandedDestSection(expanded ? 'entryScreenRules' : false)}
              sx={{ '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                  <WithGuideHelp
                    guideKey={WEBPLACEMENT_ENTRY_RULES_ANCHOR.guideKey}
                    guideService={guideService}
                    data-testid={WEBPLACEMENT_ENTRY_RULES_ANCHOR.testId}
                    position="inline-end"
                    stopClickPropagation
                    iconComponent="span"
                  >
                    <Typography>Entry Screen Rules</Typography>
                  </WithGuideHelp>
                  {formState.entryScreenRules.filter(r => r.isActive).length > 0 && (
                    <Chip
                      label={`${formState.entryScreenRules.filter(r => r.isActive).length} rules`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <EntryScreenRulesSection
                  rules={formState.entryScreenRules}
                  onChange={(entryScreenRules) => setFormState((prev) => ({ ...prev, entryScreenRules }))}
                  urlParams={formState.urlParams}
                  availableScreens={v2StepNodes}
                  disabled={loading || loadingVariables}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Variable Bindings */}
          {onboardingSchemaStatus === 'v2' && (
            <Accordion
              data-testid="wp-section-variable-bindings"
              expanded={expandedDestSection === 'variableBindings'}
              onChange={(_, expanded) => setExpandedDestSection(expanded ? 'variableBindings' : false)}
              sx={{ '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                  <Typography>Variable Bindings</Typography>
                  {formState.variableBindings.length > 0 && (
                    <Chip
                      label={`${formState.variableBindings.length} bindings`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <VariableBindingsSection
                  bindings={formState.variableBindings}
                  onChange={(variableBindings) => setFormState((prev) => ({ ...prev, variableBindings }))}
                  availableVariables={availableVariables}
                  urlParams={formState.urlParams}
                  serverData={formState.serverData}
                  disabled={loading || loadingVariables}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Webhooks */}
          <Accordion
            data-testid="wp-section-webhooks"
            expanded={expandedDestSection === 'webhooks'}
            onChange={(_, expanded) => setExpandedDestSection(expanded ? 'webhooks' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Webhooks</Typography>
                {formState.webhooks.length > 0 && (
                  <Chip
                    label={`${formState.webhooks.length} webhooks`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <WebhooksSection
                availableParameters={allAvailableParameters}
                availableScreens={v2StepNodes}
                webhooks={formState.webhooks}
                onChange={(webhooks) => setFormState((prev) => ({ ...prev, webhooks }))}
                disabled={loading || loadingVariables}
              />
              {errors.webhooks && (
                <Box
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: '0.75rem',
                    mt: theme.spacing(1),
                  }}
                >
                  {errors.webhooks}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ================================================================= */}
        {/* ADVANCED CONFIGURATION */}
        {/* ================================================================= */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Advanced Configuration (Optional)
          </Typography>

          {/* SEO Section */}
          <Accordion
            data-testid="wp-section-seo"
            expanded={expandedAdvancedSection === 'seo'}
            onChange={(_, expanded) => setExpandedAdvancedSection(expanded ? 'seo' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>SEO Settings</Typography>
                {formState.seo && (
                  <Chip label="Configured" size="small" color="primary" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <SEOEditor
                value={formState.seo}
                onChange={(seo) => setFormState((prev) => ({ ...prev, seo }))}
                disabled={loading}
              />
            </AccordionDetails>
          </Accordion>

          {/* GDPR Consent Management Section */}
          <Accordion
            data-testid="wp-section-consent"
            expanded={expandedAdvancedSection === 'gdpr'}
            onChange={(_, expanded) => setExpandedAdvancedSection(expanded ? 'gdpr' : false)}
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PolicyIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                <Typography>GDPR Consent Management</Typography>
                {formState.consentManagement ? (
                  <Chip
                    label="Enabled"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ) : (
                  <Chip label="Not configured" size="small" color="default" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <ConsentManagementEditor
                value={formState.consentManagement}
                onChange={(consentManagement) => setFormState((prev) => ({ ...prev, consentManagement }))}
                disabled={loading}
              />
            </AccordionDetails>
          </Accordion>

          {/* User Data Persistence (V2 only) */}
          <Box sx={{ mt: 1, px: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.saveUserData}
                  onChange={(e) => setFormState((prev) => ({ ...prev, saveUserData: e.target.checked }))}
                  disabled={loading}
                />
              }
              label="Save user progress"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
              When enabled, user progress is saved in the browser. Users returning to the funnel
              resume where they left off. When disabled, the funnel always starts from the beginning.
            </Typography>
          </Box>

          {/* Payment Method Persistence - Upsell (V2 only) */}
          <Box sx={{ mt: 1, px: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.rememberPaymentMethod}
                  onChange={(e) => setFormState((prev) => ({ ...prev, rememberPaymentMethod: e.target.checked }))}
                  disabled={loading}
                  data-testid="wp-remember-payment-method-switch"
                />
              }
              label="Remember payment method"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
              When enabled, the payment method is remembered during the session for frictionless
              upsell purchases. When disabled, every purchase screen shows the full payment form.
            </Typography>
          </Box>
        </Box>

        {/* Info about deployment */}
        <Alert severity="info">
          After saving, you can deploy this web funnel to make it accessible at the URL above. The
          funnel will be served as a standalone web page.
        </Alert>
      </Box>
    </SDialog>
  );
};

export default WebPlacementDialog;
